import React, { useMemo } from 'react';
import { Box, Skeleton, Stack, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { NEEView, Unit } from '../../types/enums';
import { BaselineAnalysisData } from '../../types/schema';
import { ChartContainer, BarPlot, ChartsGrid, ChartsReferenceLine, ChartsAxis, ChartsTooltip } from '@mui/x-charts';

interface BaselineChartProps {
  data: BaselineAnalysisData['chartData'];
  baselineAverage: number | null;
  view: NEEView;
  unit: Unit;
  height?: number;
  loading?: boolean;
}

const BaselineChart: React.FC<BaselineChartProps> = ({
  data,
  baselineAverage,
  view,
  unit,
  height = 400,
  loading = false,
}) => {
  const theme = useTheme();
  const scale = unit === Unit.KTCO2 ? 1000 : 1;

  const safeData = Array.isArray(data) ? data : [];

  const rawData = (view === NEEView.COMPARISON
      ? safeData.map(d => d.annualNee / scale)
      : safeData.map(d => d.differenceFromBaseline / scale)
  );

  const yAxisDomain = useMemo(() => {
    if (safeData.length === 0) {
      return [0, 0];
    }
    const allValues = [...rawData];
    if (view === NEEView.COMPARISON && baselineAverage !== null) {
      allValues.push(baselineAverage / scale);
    }
    const min = Math.min(...allValues, 0);
    const max = Math.max(...allValues, 0);
    const padding = Math.abs(max - min) * 0.1;
    return [min - padding, max + padding];
  }, [rawData, baselineAverage, view, scale, safeData.length]);

  if (loading) {
    return (
      <Stack spacing={1} sx={{ p: 2, height }}>
        <Skeleton variant="rectangular" height={height * 0.8} animation="wave" />
        <Skeleton variant="text" width="60%" animation="wave" />
        <Skeleton variant="text" width="40%" animation="wave" />
      </Stack>
    );
  }

  if (safeData.length === 0) {
    return (
      <Box sx={{ height, display: 'flex', alignItems: 'center', justifyContent: 'center', border: '1px dashed', borderColor: 'divider' }}>
        <Typography color="text.secondary">No data available for the selected criteria.</Typography>
      </Box>
    );
  }

  const xAxisData = safeData.map(d => d.year);
  const yAxisLabel = unit === Unit.TCO2 ? 'NEE Total (tCO₂)' : 'NEE Total (ktCO₂)';

  const valueFormatter = (value: number | null) => (value !== null ? value.toFixed(2) : '');

  return (
    <Box sx={{ width: '100%', height }}>
        <ChartContainer
            series={[
                {
                    type: 'bar',
                    data: rawData,
                    label: (location) => location === 'tooltip' ? yAxisLabel : '',
                    valueFormatter,
                },
            ]}
            xAxis={[{
                data: xAxisData,
                scaleType: 'band',
                label: 'Year',
                tickPlacement: 'middle',
                tickLabelPlacement: 'middle',
                disableLine: true,
                valueFormatter: (value) => {
                    // Format year without comma separator
                    if (typeof value === 'number') {
                        return value.toString();
                    }
                    return String(value);
                },
            }]}
            yAxis={[{
                label: yAxisLabel,
                min: yAxisDomain[0],
                max: yAxisDomain[1],
                valueFormatter: (value) => value.toFixed(0),
                colorMap: {
                    type: 'piecewise',
                    thresholds: [0],
                    // Negative values (< 0) = green, Positive values (>= 0) = red
                    colors: [theme.palette.success.main, theme.palette.error.main],
                },
            }]}
            height={height}
            margin={{ top: 20, right: 20, bottom: 60, left: 80 }}
            sx={{
              '& .MuiChartsAxis-left .MuiChartsAxis-label': {
                transform: 'translateX(-30px)',
              },
              '& .MuiBarElement-root': {
                clipPath: `inset(0px round 4px 4px 0px 0px)`,
              },
              '& .MuiChartsGrid-line': {
                stroke: 'rgba(150, 150, 150, 0.4)',
                strokeDasharray: '0',
              },
            }}
        >
            <ChartsGrid horizontal />
            <BarPlot />
            <ChartsAxis />
            <ChartsTooltip />
            {view === NEEView.COMPARISON && baselineAverage !== null && (
                <ChartsReferenceLine
                    y={baselineAverage / scale}
                    label="Baseline"
                    lineStyle={{ stroke: '#ff8c00', strokeDasharray: '5 5' }}
                    labelStyle={{ fill: '#ff8c00' }}
                />
            )}
        </ChartContainer>
    </Box>
  );
};

export default BaselineChart;
